/**
 * Runtime stage gate for the Session Flight Recorder (AVS-4426).
 *
 * The Story's cross-cutting rule exists because the obvious design fails OPEN. `utils/ui/stage.ts:6`
 * is `(process.env.STAGE as IStage) || 'dev'`: if `vars.STAGE` were unset, renamed or typo'd for the
 * prod environment, a `!== 'prod'` build gate would SHIP the recorder and that `|| 'dev'` fallback
 * would ALLOW it — two gates sharing one failure. So this module:
 *
 * - reads the stage **strictly**, with no default of any kind, and
 * - accepts a **positive allowlist** (`dev`, `prelive`), so anything unrecognised is refused, and
 * - refuses `import.meta.env.MODE === 'test'`.
 *
 * That last one is not belt-and-braces. `.github/workflows/pr.yml` runs vitest with `STAGE: 'dev'`,
 * so the stage half of the gate is genuinely OPEN under CI, and a module-scope install would patch
 * `globalThis.fetch` and `window.onerror` in every suite that imports the graph.
 *
 * `EXHIBITION_STAGE` is deliberately not reused, and neither is the hostname-based
 * `harness/dcc/stageGate.ts`: inside a partner dealer portal the hostname is theirs and resolves to
 * `'unknown'`.
 *
 * This is gate 2 of 3. Gate 1 is the build allowlist in `vite.config.ts` (the recorder is simply
 * absent from every other artefact); gate 3 is `Config.telemetryEnabled` in the infra, owned by
 * AVS-4427.
 */

/**
 * The only stage that may record. Positive allowlist — everything else is refused.
 *
 * `prelive` is off for now (2026-09-22) to check whether the recorder causes issues there. To turn
 * it back on, add it here and to `RECORDER_STAGES` in `scripts/recorder-build-gate.mjs`.
 */
export const RECORDER_STAGES: readonly string[] = ['dev'];

/**
 * Every stage literal the pipeline knows (`infra/types.ts:5`). A value outside this set means a
 * typo or a rename, which the build-time assertion in `scripts/recorder-build-gate.mjs` rejects.
 */
export const KNOWN_STAGES: readonly string[] = ['test', 'dev', 'prelive', 'prod', 'pr'];

/**
 * Values `process.env.STAGE` can hold that mean "unset".
 *
 * Verified by direct probe, and surprising enough to be worth naming: `vite.config.ts` defines
 * `process.env.STAGE` as `JSON.stringify(env.STAGE)`, and with `STAGE` unset that lands in the
 * bundle as the **string `"undefined"`**, not the value `undefined`. The allowlist refuses it
 * either way — this set exists only so the probe's hint says "unset" instead of quoting the word.
 */
const UNSET_STAGE_VALUES: readonly unknown[] = [undefined, null, '', 'undefined', 'null'];

/**
 * The stage, read strictly.
 *
 * Vite replaces `process.env.STAGE` with a build-time literal (see {@link UNSET_STAGE_VALUES} for
 * what an unset one becomes). **There is no `|| 'dev'` here and there must never be one.**
 */
export function readStage(): unknown {
  return process.env.STAGE;
}

/** True for every spelling of "nobody set STAGE". */
export function isStageUnset(stage: unknown): boolean {
  return UNSET_STAGE_VALUES.includes(stage);
}

/** The Vite mode. `'test'` under vitest, `'development'` on the dev server, else the build mode. */
export function readMode(): string {
  return import.meta.env.MODE;
}

/** Positive allowlist membership. Anything that is not exactly `'dev'` or `'prelive'` is refused. */
export function isRecorderStage(stage: unknown): boolean {
  return typeof stage === 'string' && RECORDER_STAGES.includes(stage);
}

/**
 * May the recorder install and capture in this process? Reads the ambient build-time stage.
 *
 * This is the production entry point and it takes NO arguments, deliberately. It used to be
 * `isRecorderAllowed(stage = readStage(), mode = readMode())`, which reads as "pass a stage to
 * override" and is not what JavaScript does: a default parameter is applied whenever the argument
 * is `undefined`, whether or not the caller passed it. So `isRecorderAllowed(undefined, mode)` fell
 * back to the AMBIENT stage instead of evaluating `undefined`, and the single most important row of
 * the refusal matrix — STAGE unset, the fail-open case this whole gate exists to close — asserted
 * nothing at all.
 *
 * It passed locally, where the ambient STAGE happens to be unset, and failed only on CI, where
 * `pr.yml` sets `STAGE: 'dev'`. Splitting the ambient read from the pure evaluation is what makes
 * the unset case expressible: {@link gateReasonFor} has no defaults to fall back to.
 */
export function isRecorderAllowed(): boolean {
  return isRecorderAllowedFor(readStage(), readMode());
}

/** Pure refusal matrix. Both arguments REQUIRED, so `undefined` means unset and nothing else. */
export function isRecorderAllowedFor(stage: unknown, mode: string): boolean {
  return isRecorderStage(stage) && mode !== 'test';
}

/**
 * Why the gate refused, in words, for the console probe's hint. Returns `null` when it did not.
 * Ambient counterpart of {@link gateReasonFor}; see {@link isRecorderAllowed} for why they are
 * separate functions rather than one with defaults.
 */
export function recorderGateReason(): string | null {
  return gateReasonFor(readStage(), readMode());
}

/** Pure refusal reason. Both arguments REQUIRED — an explicit `undefined` is a genuine unset stage. */
export function gateReasonFor(stage: unknown, mode: string): string | null {
  if (mode === 'test') {
    return 'refused: running under vitest (import.meta.env.MODE === "test")';
  }

  if (isStageUnset(stage) || typeof stage !== 'string') {
    return 'refused: STAGE is unset — the gate does not default';
  }

  if (!isRecorderStage(stage)) {
    return `refused: STAGE "${stage}" is not in the recorder allowlist (${RECORDER_STAGES.join(', ')})`;
  }

  return null;
}
